<template>
    <VTextarea
        v-model="val"
        @change="handleChange"
        :variant="variant"
        :label="label"
        :placeholder="placeholder"
        :persistentPlaceholder="persistent"
        :density="density"
        :hint="hint"
        :prefix="prefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :readonly="readonly"
        :clearable="clearable"
        :loading="loading"
        :errorMessages="errorMessages"
        :rows="rows"
        :noResize="noResize" />
</template>

<script lang="ts">
    import { computed, defineComponent, watch, ref, PropType } from 'vue';
    import { VTextarea } from 'vuetify/components';
    import { Variant, Density } from './Textarea.interfaces';

    export default defineComponent({
        name: 'Textarea',
        components: { VTextarea },
        emits: ['update:modelValue'],
        props: {
            modelValue: { type: String as () => string | null },
            label: String,
            placeholder: String,
            hint: String,
            prefix: String,
            suffix: String,
            rows: { type: Number, default: 3 },
            style: { type: Object as PropType<any> },
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            noResize: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
        },
        setup(props, { emit }) {
            const val = ref<string | undefined | null>(props.modelValue);
            const persistent = computed(() => Boolean(val.value));

            function handleChange() {
                if (val.value) {
                    val.value = val.value.trim();
                }
            }

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    if (newValue !== prevValue) {
                        val.value = newValue;
                    }
                }
            );

            watch(
                () => val.value,
                (newValue, prevValue) => {
                    if (newValue !== prevValue) {
                        emit('update:modelValue', newValue);
                    }
                }
            );

            return {
                val,
                persistent,
                handleChange,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @use '@/styles/import.scss' as *;

    :deep(.v-field) {
        @include font-body-m();
    }
</style>
